import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Clock3 } from "lucide-react";
import { Button } from "./ui/button";
import VerificationDialog from "./VerificationDialog";
import { verificationProgress } from '../lib/verificationProgress';
export default function VerificationStatus({ customer, verificationMethod = 'DOCUMENT', compactIcon = false, className = 'rounded-2xl border bg-white p-5 shadow-sm' }) {
  const navigate = useNavigate();
  const [details, setDetails] = useState(false);
  const socialAccount = ['GOOGLE', 'FACEBOOK'].includes(customer?.userId?.authProvider);
  const otpAccount = socialAccount || customer?.userId?.authProvider === 'MOBILE';
  const verified = customer?.status === "VERIFIED";
  const declined = verificationMethod === "DOCUMENT" && !otpAccount && !verified && ["REJECTED", "DECLINED"].includes(customer?.status);
  const pending = !verified && customer?.status === "UNDER_REVIEW" && verificationMethod === "DOCUMENT" && !otpAccount;
  const Icon = verified ? ShieldCheck : pending ? Clock3 : ShieldAlert;
  const account = customer?.userId || {};
  const progress = verificationProgress({ ...account, details: account.accountVerification, phoneVerified: Boolean(account.phoneVerifiedAt), emailVerified: Boolean(account.emailVerifiedAt) });
  return (
    <section className={className}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className={
              (compactIcon ? "shrink-0 " : "rounded-xl p-3 ") +
              (verified
                ? (compactIcon ? "text-emerald-700" : "bg-emerald-50 text-emerald-700")
                : (compactIcon ? "text-amber-700" : "bg-amber-50 text-amber-700"))
            }
          >
            <Icon className={compactIcon ? "h-5 w-5" : "h-6 w-6"} aria-hidden="true" />
          </div>
          <div>
            <h2 className="font-semibold">
              {verified ? "Account Verified" : pending ? "Account Under Review" : "Account Unverified"}
            </h2>
            <span
              className={
                "mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-semibold " +
                (verified
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700")
              }
            >
              {verified ? "Verified" : pending ? "Under Review" : "Unverified"}
            </span>
          </div>
        </div>
        {!verified && !pending && (
          <Button onClick={() => navigate("/account-verification")}>
            Get verified
          </Button>
        )}
      </div>
      {!verified && !pending && (otpAccount || verificationMethod === 'OTP') && <p className="mt-3 text-sm text-amber-800">Still needed: {progress.missing.length ? progress.missing.join(', ') + '.' : 'Final account confirmation.'}</p>}
      {pending && (
        <p className="mt-4 text-sm text-slate-600">
          Verification submitted. Your documents are under review.
        </p>
      )}
      {declined && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-red-50 p-3">
          <span className="font-medium text-red-700">
            Verification Declined
          </span>
          <Button variant="outline" onClick={() => setDetails(true)}>
            See details
          </Button>
        </div>
      )}
      {details && (
        <VerificationDialog
          title="Verification Declined"
          onClose={() => setDetails(false)}
        >
          <p className="mb-3 text-sm text-slate-600">
            Please address these issues before submitting your documents again.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            {(customer.verification?.rejectionReasons || []).map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          <Button
            className="mt-6"
            onClick={() => navigate("/account-verification")}
          >
            Submit again
          </Button>
        </VerificationDialog>
      )}
    </section>
  );
}
